import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import api from "../../api/axios";

/* The admin-account endpoints. Previously `API_URL` was a second import of
   the axios INSTANCE, so it string-coerced into the interceptor's source
   text and every URL built from it was garbage. */
const ADMIN_ACCOUNTS_URL = "/admin/accounts";

/* Sign-in is [no auth] and answers a wrong password with a REAL 401. `api`'s
   response interceptor turns any 401 into a token wipe and a full-page
   redirect, and its one exemption still names the retired
   /api/adminuser/login — so the login call goes out on a bare client that
   shares only the base URL, exactly as slice/adminUser does. */
const anon = axios.create({ baseURL: api.defaults.baseURL });

/* Failure is a real HTTP status with a { code, message } body; there is no
   status field inside the body any more. Some codes carry no message. */
const errorText = (err, fallback) => {
  const data = err.response?.data;
  return data?.message || data?.code || fallback;
};

/* =========================
   ADMIN LOGIN API
========================= */
export const loginUser = createAsyncThunk(
  "user/login",
  async (payload, thunkAPI) => {
    try {
      const res = await anon.post("/auth/admin/login", payload);

      /* Two 200s carry no token: an account with 2FA on answers
         { requires2fa, challenge, username } and one told to enrol answers
         { requiresEnrolment, challenge, username }. Storing either would
         write "undefined" as the token. Both are finished on the login
         screen through slice/adminUser's verify/enrol thunks. */
      if (res.data?.requires2fa || res.data?.requiresEnrolment) return res.data;

      /* The identity moved into a nested `user`; role is still 1, 2 or 3. */
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("userId", res.data.user.id);
      localStorage.setItem("role", res.data.user.role);

      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(errorText(err, "Login failed"));
    }
  }
);

/* =========================
   CREATE USER API
========================= */
export const createUser = createAsyncThunk(
  "user/createUser",
  async (payload, thunkAPI) => {
    try {
      /* AccountBody is (username, password, role, storeId, phone, active) —
         there is no createdBy field any more, the audit trigger records the
         actor off the token instead. */
      const res = await api.post(ADMIN_ACCOUNTS_URL, payload);

      /* 201 with { message, data: { id, username, role, storeId, isActive } }
         — this handler's own payload is the wrapper, so res.data is it. */
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(errorText(err, "Failed to create user"));
    }
  }
);

/* =========================
   FETCH USERS API
========================= */
export const fetchUsers = createAsyncThunk(
  "user/fetchUsers",
  async (_, thunkAPI) => {
    try {
      const res = await api.get(ADMIN_ACCOUNTS_URL);
      /* One of the handlers whose OWN top-level key is `data`:
         { message, data: rows }. res.data.data is correct here, not a
         leftover Node wrapper. A row is { id, username, role, storeId,
         phone, isActive, totpEnabled, totpRequired, totpEnabledAt,
         lastLoginAt, lockedUntil } — `id`, never `_id`. */
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(errorText(err, "Failed to fetch users"));
    }
  }
);

/* =========================
   UPDATE PROFILE API
========================= */
export const updateProfile = createAsyncThunk(
  "user/updateProfile",
  async (payload, thunkAPI) => {
    try {
      /* /api/auth/profile edited the customer record, and its successor is
         the customer's own profile route. It takes { name, email } — either
         one alone is fine, absent means leave unchanged — and it refuses a
         caller that is not a customer with 403 not_a_customer. */
      const res = await api.put("/customer/profile", payload);

      /* The profile itself now, not { user }:
         { id, phoneNumber, name, email, createdAt, lastLoginAt }. */
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(errorText(err, "Failed to update profile"));
    }
  }
);

/* =========================
   SLICE
========================= */
const userSlice = createSlice({
  name: "user",
  initialState: {
    adminuser: [],
    user: null,           // ← stores logged-in user data
    token: localStorage.getItem("token") || null,
    loading: false,
    success: false,
    error: null,
  },
  reducers: {
    resetUserStatus: (state) => {
      state.success = false;
      state.error = null;
    },
    logout: (state) => {
      state.token = null;
      state.user = null;
      state.adminuser = [];
      localStorage.clear();
    },
  },
  extraReducers: (builder) => {
    builder
      /* =========================
         LOGIN
      ========================= */
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        /* A second factor is not a session: that branch carries a challenge
           and no token, so the stored one is left alone rather than
           overwritten with undefined. */
        if (action.payload?.token) state.token = action.payload.token;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* =========================
         CREATE USER
      ========================= */
      .addCase(createUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(createUser.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* =========================
         FETCH USERS
      ========================= */
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.adminuser = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* =========================
         UPDATE PROFILE
      ========================= */
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
  .addCase(updateProfile.fulfilled, (state, action) => {
  state.loading = false;
  state.success = true;
  state.user = action.payload;

  localStorage.setItem("user", JSON.stringify(action.payload));
})
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetUserStatus, logout } = userSlice.actions;
export default userSlice.reducer;